import { ChangeDetectionStrategy, Component, computed, inject, input, output, ResourceLoaderParams } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateModule } from '@ngx-translate/core';
import { DocumentDirection } from '@phx-erp/shared';
import type { ChartData, ChartOptions } from 'chart.js';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, of } from 'rxjs';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { UiLocaleService } from '../../../../@core/services/i18n/ui-locale.service';
import { PhxChartComponent } from '../../../../shared/components/phx-chart/phx-chart.component';
import { PhxStatCardColor } from '../../../../shared/components/phx-stat-card/phx-stat-card-theme';
import { PhxStatCardComponent } from '../../../../shared/components/phx-stat-card/phx-stat-card.component';
import { LocaleCurrencyPipe } from '../../../../shared/pipes/locale-currency.pipe';
import { LocaleDatePipe } from '../../../../shared/pipes/locale-date.pipe';
import {
  GetLineItemPriceHistoryQuery,
  LineItemAnalyticsService,
  LineItemPriceHistoryApplyPayload,
  LineItemPriceHistoryData,
  LineItemPriceHistoryEntryRow,
  LineItemPriceHistoryQueryInput,
} from '../../services/line-item-analytics.service';

interface PriceHistoryRequest {
  productId: string;
  customerId?: string;
  documentDirection?: DocumentDirection;
  documentType?: string;
}

@Component({
  selector: 'app-widget-line-item-price-history',
  templateUrl: './widget-line-item-price-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    LocaleCurrencyPipe,
    LocaleDatePipe,
    PhxChartComponent,
    PhxStatCardComponent,
    ProgressSpinnerModule,
    RouterLink,
    TableModule,
    TooltipModule,
    TranslateModule,
  ],
})
export class WidgetLineItemPriceHistoryComponent {
  productId = input.required<string>();
  customerId = input<string | null | undefined>(undefined);
  documentDirection = input<DocumentDirection | null | undefined>(DocumentDirection.OUT);
  documentType = input<string | null | undefined>(undefined);
  applyPriceEnabled = input(true);

  unitPriceApply = output<LineItemPriceHistoryApplyPayload>();

  private readonly lineItemAnalyticsService = inject(LineItemAnalyticsService);
  private readonly uiLocale = inject(UiLocaleService);
  private readonly i18n = inject(I18nService);

  private readonly request = computed((): PriceHistoryRequest | null => {
    const productId = this.productId();
    if (!productId) {
      return null;
    }
    return {
      productId,
      customerId: this.customerId() ?? undefined,
      documentDirection: this.documentDirection() ?? DocumentDirection.OUT,
      documentType: this.documentType() ?? undefined,
    };
  });

  readonly priceHistoryResource = rxResource<LineItemPriceHistoryData | null, PriceHistoryRequest | null>({
    params: () => this.request(),
    stream: ({ params }: ResourceLoaderParams<PriceHistoryRequest | null>) => {
      if (!params?.productId) {
        return of(null);
      }

      const input: LineItemPriceHistoryQueryInput = {
        productId: params.productId,
        customerId: params.customerId,
        documentDirection: params.documentDirection,
        documentTypes: params.documentType ? [params.documentType] : undefined,
        itemsCount: 50,
      };

      return this.lineItemAnalyticsService
        .getLineItemPriceHistory(input)
        .mapSingle((data: GetLineItemPriceHistoryQuery) => data.lineItemPriceHistory)
        .pipe(
          catchError((error: unknown) => {
            console.error(
              `Failed to load line item price history for productId: ${params.productId}, customerId: ${params.customerId ?? 'n/a'}`,
              error,
            );
            return of(null);
          }),
        );
    },
  });

  readonly items = computed(() => this.priceHistoryResource.value()?.items ?? []);
  readonly summary = computed(() => this.priceHistoryResource.value()?.summary ?? null);
  readonly isLoading = computed(() => this.priceHistoryResource.isLoading());
  readonly loadFailed = computed(
    () => !this.isLoading() && this.request() != null && this.priceHistoryResource.value() === null,
  );
  readonly isEmpty = computed(
    () => !this.isLoading() && !this.loadFailed() && this.items().length === 0,
  );

  readonly showMixedDocumentTypes = computed(() => {
    const types = new Set(
      this.items()
        .map(item => item.documentType)
        .filter((type): type is string => type != null && type !== ''),
    );
    return types.size > 1;
  });

  readonly emptyMessageKey = _('document-line-item.price-history.empty');
  readonly loadFailedMessageKey = _('document-line-item.price-history.load-failed');
  readonly applyPriceTooltipKey = _('document-line-item.price-history.apply-price-tooltip');
  readonly applyPriceDisabledTooltipKey = _('document-line-item.price-history.apply-price-disabled-tooltip');

  readonly statCurrency = computed(() => this.summary()?.currency ?? undefined);

  readonly statAvgUnitPrice = computed(() =>
    this.formatCurrencyOrDash(this.summary()?.avgUnitPrice, this.statCurrency()),
  );

  readonly statPeakUnitPrice = computed(() =>
    this.formatCurrencyOrDash(this.summary()?.peakUnitPrice, this.statCurrency()),
  );

  readonly statCurrentDelta = computed(() =>
    this.formatCurrencyOrDash(this.summary()?.currentDelta, this.statCurrency()),
  );

  readonly statCurrentDeltaBadge = computed(() =>
    this.formatSignedPercent(this.summary()?.currentDeltaPercent),
  );

  readonly statCurrentDeltaColor = computed((): PhxStatCardColor => 'violet');

  readonly chartData = computed((): ChartData<'line'> | null => {
    const rows = [...this.items()].reverse();
    if (rows.length === 0) {
      return null;
    }

    const documentStyle = getComputedStyle(document.documentElement);
    const unitPriceColor = documentStyle.getPropertyValue('--p-sky-500').trim() || '#0ea5e9';
    const referenceListPriceColor = documentStyle.getPropertyValue('--p-violet-500').trim() || '#8b5cf6';
    const documentListPriceColor = documentStyle.getPropertyValue('--p-red-500').trim() || '#ef4444';
    const quantityColor = documentStyle.getPropertyValue('--p-amber-500').trim() || '#f59e0b';

    const labels = rows.map(row => this.formatChartLabel(row.date, row.documentIdentifier));
    const hasReferenceListPrices = rows.some(row => row.referenceListUnitPrice != null);
    const hasDocumentListPrices = rows.some(row => row.documentPriceListUnitPrice != null);

    const datasets: ChartData<'line'>['datasets'] = [
      {
        label: this.i18n.translate(_('document-line-item.price-history.unit-price')),
        data: rows.map(row => row.unitPrice ?? null),
        yAxisID: 'y',
        tension: 0.25,
        borderColor: unitPriceColor,
        backgroundColor: unitPriceColor,
        pointBackgroundColor: unitPriceColor,
        pointBorderColor: unitPriceColor,
        pointRadius: 4,
        pointHoverRadius: 6,
        spanGaps: true,
      },
    ];

    if (hasReferenceListPrices) {
      datasets.push({
        label: this.i18n.translate(_('document-line-item.price-history.chart-reference-list-price')),
        data: rows.map(row => row.referenceListUnitPrice ?? null),
        yAxisID: 'y',
        tension: 0.25,
        borderColor: referenceListPriceColor,
        backgroundColor: referenceListPriceColor,
        pointBackgroundColor: referenceListPriceColor,
        pointBorderColor: referenceListPriceColor,
        borderDash: [6, 4],
        pointRadius: 3,
        pointHoverRadius: 5,
        spanGaps: true,
      });
    }

    if (hasDocumentListPrices) {
      datasets.push({
        label: this.i18n.translate(_('document-line-item.price-history.chart-document-price-list-price')),
        data: rows.map(row => row.documentPriceListUnitPrice ?? null),
        yAxisID: 'y',
        tension: 0.25,
        borderColor: documentListPriceColor,
        backgroundColor: documentListPriceColor,
        pointBackgroundColor: documentListPriceColor,
        pointBorderColor: documentListPriceColor,
        borderDash: [2, 3],
        pointRadius: 3,
        pointHoverRadius: 5,
        spanGaps: true,
      });
    }

    datasets.push({
      label: this.i18n.translate(_('document-line-item.price-history.quantity')),
      data: rows.map(row => row.quantity ?? null),
      yAxisID: 'y1',
      tension: 0.25,
      borderColor: quantityColor,
      backgroundColor: quantityColor,
      pointBackgroundColor: quantityColor,
      pointBorderColor: quantityColor,
      pointRadius: 3,
      pointHoverRadius: 5,
      spanGaps: true,
    });

    return { labels, datasets };
  });

  readonly chartOptions = computed((): ChartOptions<'line'> => {
    const currency = this.statCurrency();
    const uiLocale = this.uiLocale;
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-primary-500').trim();
    const textColorSecondary = documentStyle.getPropertyValue('--p-slate-400').trim();
    const surfaceBorder = documentStyle.getPropertyValue('--p-slate-100').trim();

    return {
      maintainAspectRatio: false,
      responsive: true,
      animation: {
        duration: 750,
        easing: 'easeOutCubic',
      },
      animations: {
        y: {
          type: 'number',
          duration: 750,
          easing: 'easeOutCubic',
          from: ctx => {
            if (ctx.type !== 'data' || ctx.mode !== 'default') {
              return undefined;
            }
            const axisId = ctx.dataset.yAxisID ?? 'y';
            const scale = ctx.chart.scales[axisId];
            return scale?.min ?? 0;
          },
        },
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: textColor,
            boxWidth: 12,
            usePointStyle: true,
          },
        },
        tooltip: {
          callbacks: {
            label: context => {
              const label = context.dataset.label ?? '';
              const value = context.parsed.y;
              if (value == null || Number.isNaN(value)) {
                return `${label}: —`;
              }
              if (context.dataset.yAxisID === 'y1') {
                return `${label}: ${this.formatChartQuantity(value)}`;
              }
              if (currency) {
                return `${label}: ${uiLocale.formatCurrency(value, currency)}`;
              }
              return `${label}: ${value}`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 12,
          },
          grid: {
            color: surfaceBorder,
          },
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          grace: '5%',
          ticks: {
            color: textColorSecondary,
            callback: value =>
              currency ? uiLocale.formatCurrency(Number(value), currency) : String(value),
          },
          grid: {
            color: surfaceBorder,
          },
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          grace: '5%',
          ticks: {
            color: textColorSecondary,
            callback: value => this.formatChartQuantity(Number(value)),
          },
          grid: {
            drawOnChartArea: false,
            color: surfaceBorder,
          },
        },
      },
    };
  });

  private formatChartQuantity(value: number): string {
    return new Intl.NumberFormat(this.uiLocale.intlLocale, {
      maximumFractionDigits: 4,
    }).format(value);
  }

  private formatChartLabel(date: string | null | undefined, documentIdentifier: string | null | undefined): string {
    const dateLabel =
      date != null
        ? new Intl.DateTimeFormat(this.uiLocale.intlLocale, { dateStyle: 'short' }).format(new Date(date))
        : '—';
    if (documentIdentifier) {
      return `${dateLabel} · ${documentIdentifier}`;
    }
    return dateLabel;
  }

  private formatCurrencyOrDash(
    value: number | null | undefined,
    currency: string | undefined,
  ): string {
    if (value == null || !currency) {
      return '—';
    }
    return this.uiLocale.formatCurrency(value, currency);
  }

  private formatSignedPercent(value: number | null | undefined): string | undefined {
    if (value == null) {
      return undefined;
    }
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  }

  rowDeltaTrend(deltaPercent: number | null | undefined): 'up' | 'down' | 'neutral' | null {
    if (deltaPercent == null) {
      return null;
    }
    if (deltaPercent > 0) {
      return 'up';
    }
    if (deltaPercent < 0) {
      return 'down';
    }
    return 'neutral';
  }

  rowDeltaClass(deltaPercent: number | null | undefined): string {
    switch (this.rowDeltaTrend(deltaPercent)) {
      case 'up':
        return 'text-emerald-600 dark:text-emerald-400';
      case 'down':
        return 'text-red-600 dark:text-red-400';
      case 'neutral':
        return 'text-slate-500 dark:text-slate-400';
      default:
        return '';
    }
  }

  formatRowDeltaPercent(deltaPercent: number | null | undefined): string | null {
    if (deltaPercent == null) {
      return null;
    }
    return `${Math.abs(deltaPercent).toFixed(1)}%`;
  }

  formatRowDiscount(
    discountPercentage: number | null | undefined,
    discountPercentage2: number | null | undefined,
  ): string | null {
    const hasDiscount1 = discountPercentage != null && discountPercentage !== 0;
    const hasDiscount2 = discountPercentage2 != null && discountPercentage2 !== 0;

    if (!hasDiscount1 && !hasDiscount2) {
      return null;
    }

    if (hasDiscount1 && hasDiscount2) {
      return `${discountPercentage}% (${discountPercentage2}%)`;
    }
    if (hasDiscount1) {
      return `${discountPercentage}%`;
    }
    return `(${discountPercentage2}%)`;
  }

  onUnitPriceClick(row: LineItemPriceHistoryEntryRow): void {
    if (!this.applyPriceEnabled() || row.unitPrice == null) {
      return;
    }
    this.unitPriceApply.emit({
      unitPrice: row.unitPrice,
      discountPercentage: row.discountPercentage,
      discountPercentage2: row.discountPercentage2,
    });
  }

  priceSourceLabel(priceSource: string | null | undefined): string {
    switch (priceSource) {
      case 'MANUALPRICE':
        return this.i18n.translate(_('document-line-item.price-history.price-source-manual'));
      case 'PRICELIST':
        return this.i18n.translate(_('document-line-item.price-history.price-source-price-list'));
      case 'CUSTOMERPRICE':
        return this.i18n.translate(_('document-line-item.price-history.price-source-customer-price'));
      case 'ACCESSORYCONFIG':
        return this.i18n.translate(_('document-line-item.price-history.price-source-accessory'));
      default:
        return priceSource ?? '';
    }
  }

  priceSourceIcon(priceSource: string | null | undefined): string | null {
    switch (priceSource) {
      case 'MANUALPRICE':
        return 'fa-solid fa-pen';
      case 'PRICELIST':
        return 'fa-solid fa-tags';
      case 'CUSTOMERPRICE':
        return 'fa-solid fa-user-tag';
      case 'ACCESSORYCONFIG':
        return 'fa-solid fa-puzzle-piece';
      default:
        return null;
    }
  }

  unitPriceTextClass(priceSource: string | null | undefined, applyEnabled: boolean): string {
    if (applyEnabled) {
      return 'text-primary';
    }
    switch (priceSource) {
      case 'MANUALPRICE':
        return 'text-orange-500 dark:text-orange-400';
      case 'PRICELIST':
        return 'text-red-600 dark:text-red-400';
      case 'CUSTOMERPRICE':
        return 'text-amber-600 dark:text-amber-400';
      default:
        return 'text-slate-700 dark:text-slate-200';
    }
  }

  unitPriceButtonClass(priceSource: string | null | undefined, applyEnabled: boolean): string {
    const base = 'rounded px-0.5 text-right font-medium transition-colors';
    if (applyEnabled) {
      return `${base} cursor-pointer hover:underline ${this.unitPriceTextClass(priceSource, true)}`;
    }
    return `${base} ${this.unitPriceTextClass(priceSource, false)}`;
  }
}
